import axios from 'axios';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  // headers: {
  //       'Content-Type': 'application/json',
  //     },
});

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

const REFRESH_PATH = '/token/refresh/';

const getAuthData = (): { access?: string; refresh?: string;[key: string]: any } | null => {
  const raw = localStorage.getItem('auth_data');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem('auth_data');
  localStorage.removeItem('access_token');
  window.dispatchEvent(new Event('auth-changed'));
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// The access token used to live 100 days, so an expired/invalid token was
// never really exercised in practice. Now that it's short-lived, any request
// can 401 mid-session; without this, that meant a permanent "Loading..."
// screen with no way back to login (axios calls just failed silently).
// This refreshes once and retries the original request; concurrent 401s
// share a single in-flight refresh instead of each firing their own.
let refreshPromise: Promise<string | null> | null = null;

const refreshAccessToken = (): Promise<string | null> => {
  const authData = getAuthData();
  if (!authData?.refresh) return Promise.resolve(null);

  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${process.env.REACT_APP_BASE_URL}${REFRESH_PATH}`, { refresh: authData.refresh })
      .then((res) => {
        const newAccess: string = res.data.access;
        // ROTATE_REFRESH_TOKENS is on, so a new refresh token comes back too
        // (and the old one is now blacklisted) — fall back to the old one
        // only if the response didn't include a new one for some reason.
        const newRefresh: string = res.data.refresh || authData.refresh;
        localStorage.setItem('access_token', newAccess);
        localStorage.setItem('auth_data', JSON.stringify({ ...authData, access: newAccess, refresh: newRefresh }));
        return newAccess;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest.url === REFRESH_PATH ||
      originalRequest._retriedAfterRefresh
    ) {
      return Promise.reject(error);
    }

    originalRequest._retriedAfterRefresh = true;
    const newAccessToken = await refreshAccessToken();

    if (!newAccessToken) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
    return axiosInstance(originalRequest);
  }
);

export default axiosInstance;
